import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import AddIcon from '@mui/icons-material/Add';
import Modal from '../Modal';
import PostedJobForm from './PostedJobForm';
import {
  PAGE_SIZE,
  jobsError,
  usePostedJobs,
  useSetPostedJobOpen,
} from '../../hooks/usePostedJobs';

const CARD = 'bg-white rounded-xl border border-gray-200 shadow-sm';
const INPUT =
  'rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-claret-500 focus:outline-none focus:ring-1 focus:ring-claret-500';
const SECONDARY =
  'rounded-md border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';

const STATUS = {
  open: { label: 'Open', style: 'bg-green-50 text-green-700' },
  closed: { label: 'Closed', style: 'bg-gray-100 text-gray-600' },
  expired: { label: 'Expired', style: 'bg-amber-50 text-amber-700' },
};

const formatDay = (value) => {
  if (!value) return '–';
  // closes_at is the end of a day in Kenya time; show that day.
  const day = new Date(new Date(value).getTime() + 3 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  return format(parseISO(day), 'd MMM yyyy');
};

// Jobs admins and the talent team found; featured in the weekly digest.
export default function PostedJobsTab() {
  const [filters, setFilters] = useState({ status: 'open', mine: false });
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState(null); // 'new' or a job
  const [confirm, setConfirm] = useState(null); // job to close
  const setOpen = useSetPostedJobOpen();

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((f) => ({ ...f, q: search.trim() }));
      setOffset(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, error } = usePostedJobs(filters, offset);
  const jobs = data?.jobs ?? [];
  const total = data?.total ?? 0;
  const set = (changes) => {
    setFilters((f) => ({ ...f, ...changes }));
    setOffset(0);
  };

  const toggle = (job, open) =>
    setOpen.mutate(
      { id: job.id, open },
      {
        onSuccess: () => {
          setConfirm(null);
          toast.success(
            open ? 'Job reopened.' : 'Job closed. It won’t be sent again.'
          );
        },
        onError: (err) => {
          setConfirm(null);
          toast.error(jobsError(err, "We couldn't change the job."));
        },
      }
    );

  return (
    <div className="space-y-4">
      <div className={`${CARD} flex flex-wrap items-center gap-3 p-4`}>
        <div className="flex gap-1">
          {[
            ['open', 'Open'],
            ['closed', 'Closed'],
            ['all', 'All'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={filters.status === key}
              onClick={() => set({ status: key })}
              className={`rounded-full border px-3 py-1 text-xs ${
                filters.status === key
                  ? 'border-claret-500 bg-claret-500 text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-700">
          <input
            type="checkbox"
            checked={filters.mine}
            onChange={(e) => set({ mine: e.target.checked })}
          />
          Added by me
        </label>
        <input
          type="search"
          aria-label="Search jobs"
          placeholder="Search title or company"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${INPUT} w-60`}
        />
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="ml-auto inline-flex items-center gap-1 rounded-md bg-claret-500 px-4 py-1.5 text-sm text-white hover:bg-claret-400"
        >
          <AddIcon fontSize="small" />
          Add a job
        </button>
      </div>
      <p className="px-1 text-xs text-gray-500">
        Jobs you add are featured in the next weekly digest for graduates they
        fit, labelled “Shared by PLP”.
      </p>

      {isLoading && (
        <div className={`${CARD} h-48 animate-pulse`} aria-label="Loading" />
      )}
      {error && (
        <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>
          {jobsError(error, "We couldn't load the jobs.")}
        </div>
      )}
      {data && !error && total === 0 && (
        <div className={`${CARD} p-10 text-center text-sm text-gray-600`}>
          No jobs here yet.
        </div>
      )}
      {data && !error && total > 0 && (
        <section className={`${CARD} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="py-2 pl-5 pr-2 font-medium">Job</th>
                  <th className="px-2 py-2 font-medium">Status</th>
                  <th className="px-2 py-2 font-medium">Closes</th>
                  <th className="px-2 py-2 font-medium">Added by</th>
                  <th className="px-2 py-2 text-right font-medium">Sent</th>
                  <th className="px-2 py-2 text-right font-medium">Clicks</th>
                  <th className="px-2 py-2 text-right font-medium">Applied</th>
                  <th className="py-2 pl-2 pr-5" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => {
                  const s = STATUS[job.status] || {
                    label: job.status,
                    style: 'bg-gray-100 text-gray-600',
                  };
                  return (
                    <tr
                      key={job.id}
                      className="border-b border-gray-100 last:border-0"
                    >
                      <td className="py-2.5 pl-5 pr-2">
                        <span className="text-gray-900">{job.title}</span>
                        <span className="block text-xs text-gray-500">
                          {[job.company, job.location]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </td>
                      <td className="px-2 py-2.5">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${s.style}`}
                        >
                          {s.label}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-2 py-2.5 text-xs text-gray-600">
                        {formatDay(job.closes_at)}
                      </td>
                      <td className="px-2 py-2.5 text-xs text-gray-600">
                        {job.added_by}
                      </td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-gray-700">
                        {job.sent ?? 0}
                      </td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-gray-700">
                        {job.clicks ?? 0}
                      </td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-gray-700">
                        {job.applications ?? 0}
                      </td>
                      <td className="whitespace-nowrap py-2.5 pl-2 pr-5 text-right">
                        <div className="flex justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => setEditing(job)}
                            className={SECONDARY}
                          >
                            Edit
                          </button>
                          {job.status === 'open' ? (
                            <button
                              type="button"
                              onClick={() => setConfirm(job)}
                              disabled={setOpen.isLoading}
                              className={SECONDARY}
                            >
                              Close
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => toggle(job, true)}
                              disabled={setOpen.isLoading}
                              title={
                                job.status === 'expired'
                                  ? 'Its closing date has passed: edit the date to reopen it.'
                                  : undefined
                              }
                              className={SECONDARY}
                            >
                              Reopen
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between border-t border-gray-200 px-5 py-2 text-xs text-gray-500">
              <span>
                {offset + 1}–{Math.min(offset + jobs.length, total)} of {total}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={offset === 0}
                  onClick={() => setOffset(offset - PAGE_SIZE)}
                  className={SECONDARY}
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  className={SECONDARY}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      <Modal
        modalOpen={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Add a job' : 'Edit job'}
      >
        {editing && (
          <PostedJobForm
            key={editing === 'new' ? 'new' : editing.id}
            job={editing === 'new' ? null : editing}
            onDone={() => setEditing(null)}
            onCancel={() => setEditing(null)}
          />
        )}
      </Modal>
      <Modal
        modalOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        title="Close this job?"
        small
      >
        {confirm && (
          <div className="space-y-3 text-left text-sm text-gray-700">
            <p>
              {confirm.title} at {confirm.company} won&apos;t be sent in any
              more digests. You can reopen it later.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirm(null)}
                className={SECONDARY}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => toggle(confirm, false)}
                disabled={setOpen.isLoading}
                className="rounded-md bg-claret-500 px-4 py-1.5 text-sm text-white hover:bg-claret-400 disabled:opacity-50"
              >
                Close job
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
